import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import apiService from '../services/api';
import { SqlResultResponse } from '../types/api';
import Logger from '../utils/logger';
import { getApiErrorMessage } from '../constants/messages';
import { useTranslation } from 'react-i18next';

interface SqlEditorSectionProps {
  deploymentId: string;
}

const SqlEditorSection: React.FC<SqlEditorSectionProps> = ({ deploymentId }) => {
  const { t } = (useTranslation as any)('deployments');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<SqlResultResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [databases, setDatabases] = useState<Array<{name: string; is_default: boolean; has_dedicated_user: boolean; user?: string}>>([]);
  // Vide tant que la liste server-side n'a pas repondu. Le seul nom de base
  // valide vient de listSqlEditorDatabases (resolu via le CRD Cluster CNPG).
  // Le composant recevait auparavant un prop `databaseName` alimente avec
  // application_name, utilise comme valeur initiale : "savana-agency" au lieu
  // de "agentBanking" -> 400 "not authorized for this deployment", l'allowlist
  // backend validant la cible. Prop supprime, ne pas le reintroduire.
  const [selectedDatabase, setSelectedDatabase] = useState<string>('');
  const [databasesLoading, setDatabasesLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Charger la liste des DBs disponibles au premier ouverture du modal
  useEffect(() => {
    if (!isModalOpen || databases.length > 0) return;
    setDatabasesLoading(true);
    apiService.listSqlEditorDatabases(deploymentId)
      .then((res) => {
        setDatabases(res.databases);
        // Sélection initiale = default (bootstrap) ou premier de la liste
        const defaultDb = res.databases.find((d) => d.is_default);
        setSelectedDatabase(defaultDb?.name || res.databases[0]?.name || '');
      })
      .catch((err) => {
        // Pas de fallback possible : sans la liste on ne connait aucun nom de
        // base valide, et deviner ferait echouer l'execution en 400.
        Logger.error('Erreur chargement DBs:', err);
        setError(getApiErrorMessage(err, 'Impossible de charger la liste des bases'));
      })
      .finally(() => setDatabasesLoading(false));
  }, [isModalOpen, deploymentId, databases.length]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Cleanup polling on unmount or modal close
  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  const handleCloseModal = () => {
    if (executing) return; // Empêcher la fermeture pendant l'exécution
    stopPolling();
    setIsModalOpen(false);
  };

  const pollResult = useCallback((jobId: string) => {
    const poll = async () => {
      try {
        const res = await apiService.getSqlQueryResult(deploymentId, jobId);
        setResult(res);

        if (res.status === 'completed' || res.status === 'failed') {
          stopPolling();
          setExecuting(false);

          if (res.status === 'failed') {
            setError(res.error || 'La requête a échoué');
          }
        }
      } catch (err: unknown) {
        Logger.error('Erreur lors du polling:', err);
        stopPolling();
        setExecuting(false);
        setError('Impossible de récupérer le résultat');
      }
    };

    poll();
    pollingRef.current = setInterval(poll, 2000);
  }, [deploymentId, stopPolling]);

  const handleExecute = async () => {
    if (!query.trim()) return;
    if (!selectedDatabase) return;   // liste pas encore chargee

    setExecuting(true);
    setResult(null);
    setError(null);
    stopPolling();

    try {
      const response = await apiService.executeSqlQuery(deploymentId, query, selectedDatabase);
      setResult({ job_id: response.job_id, status: 'pending', columns: null, rows: null, row_count: null, error: null, started_at: null, completed_at: null });
      pollResult(response.job_id);
    } catch (err: unknown) {
      Logger.error('Erreur lors de l\'exécution:', err);
      setExecuting(false);
      // getApiErrorMessage sait lire le `detail` d'un 422/400 FastAPI ;
      // `err.message` ne donnait que "Request failed with status code 4xx".
      const message = getApiErrorMessage(err, 'Erreur lors de l\'exécution de la requête');
      setError(message);
      toast.error(message);
    }
  };

  return (
    <>
      {/* Bouton d'ouverture */}
      <button
        onClick={() => setIsModalOpen(true)}
        className="w-full bg-surface-2 shadow-card rounded-card px-4 py-4 flex items-center justify-between hover:bg-surface-3 transition-colors group"
      >
        <div className="flex items-center space-x-3">
          <svg className="h-6 w-6 text-text-tertiary group-hover:text-accent-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
          </svg>
          <span className="text-sm font-medium text-text-primary">SQL Editor</span>
        </div>
        <svg className="h-5 w-5 text-text-tertiary group-hover:text-text-secondary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            {/* Overlay */}
            <div className="fixed inset-0 bg-black/60 transition-opacity" onClick={handleCloseModal} />

            {/* Modal content */}
            <div className="relative bg-surface-1 rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
                <h2 className="text-lg font-semibold text-text-primary">SQL Editor</h2>
                <button
                  onClick={handleCloseModal}
                  disabled={executing}
                  className="text-text-tertiary hover:text-text-primary transition-colors disabled:opacity-50"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-4 overflow-y-auto flex-1">
                {/* Avertissement */}
                <div className="mb-4 bg-amber-500/8 border border-amber-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      <div className="flex items-center justify-center h-8 w-8 rounded-full bg-amber-500/15">
                        <svg className="h-4 w-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-400">
                        Les requêtes seront exécutées sur la base <span className="font-mono font-semibold text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded">{selectedDatabase || '…'}</span>
                      </p>
                      <p className="text-xs text-text-tertiary mt-1.5 leading-relaxed">
                        Vous êtes entièrement responsable des requêtes exécutées. Les modifications (<span className="text-red-400 font-medium">INSERT, UPDATE, DELETE, DROP...</span>) sont irréversibles.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Sélecteur de base (visible si au moins 2 DBs, sinon on cache pour ne pas polluer) */}
                {databases.length > 1 && (
                  <div className="mb-4">
                    <label htmlFor="sql-editor-db-select" className="block text-sm font-medium text-text-secondary mb-1.5">
                      {t('sqlEditor.databaseLabel', { defaultValue: 'Base de données' }) as string}
                    </label>
                    <select
                      id="sql-editor-db-select"
                      value={selectedDatabase}
                      onChange={(e) => setSelectedDatabase(e.target.value)}
                      disabled={executing || databasesLoading}
                      className="w-full bg-surface-3 text-text-primary border border-border-primary rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:opacity-50"
                    >
                      {databases.map((d) => (
                        <option key={d.name} value={d.name}>
                          {d.name}{d.is_default ? ' (default)' : ''}{d.has_dedicated_user ? ` — user: ${d.user}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Zone de saisie SQL */}
                <div className="mb-4">
                  <textarea
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="SELECT * FROM ..."
                    rows={8}
                    maxLength={10000}
                    disabled={executing}
                    className="w-full bg-surface-3 text-text-primary border border-border-primary rounded-lg px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-accent-500 focus:border-transparent resize-vertical disabled:opacity-50"
                  />
                </div>

                {/* Bouton exécuter */}
                <div className="mb-4">
                  <button
                    onClick={handleExecute}
                    disabled={executing || !query.trim() || !selectedDatabase}
                    className="inline-flex items-center px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {executing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        {t('sqlEditor.executing')}
                      </>
                    ) : (
                      <>
                        <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Exécuter
                      </>
                    )}
                  </button>
                </div>

                {/* Statut */}
                {result && (result.status === 'pending' || result.status === 'running') && (
                  <div className="mb-4 flex items-center space-x-2 text-sm text-text-secondary">
                    <svg className="animate-spin h-4 w-4 text-accent-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>{result.status === 'pending' ? t('sqlEditor.pending') as string : t('sqlEditor.running') as string}</span>
                  </div>
                )}

                {/* Erreur */}
                {error && (
                  <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-sm text-red-400 font-mono whitespace-pre-wrap">{error}</p>
                  </div>
                )}

                {/* Résultat */}
                {result && result.status === 'completed' && result.columns && result.rows && (
                  <div>
                    <div className="overflow-x-auto rounded-lg border border-border-primary">
                      <table className="min-w-full divide-y divide-border-primary">
                        <thead className="bg-surface-3">
                          <tr>
                            {result.columns.map((col, i) => (
                              <th
                                key={i}
                                className="px-4 py-2 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
                              >
                                {col}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="bg-surface-2 divide-y divide-border-primary">
                          {result.rows.map((row, rowIdx) => (
                            <tr key={rowIdx} className="hover:bg-surface-3 transition-colors">
                              {row.map((cell, cellIdx) => (
                                <td
                                  key={cellIdx}
                                  className="px-4 py-2 text-sm text-text-primary font-mono whitespace-nowrap"
                                >
                                  {cell === null ? <span className="text-text-quaternary italic">NULL</span> : String(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-2 text-xs text-text-tertiary">
                      {result.row_count} ligne{result.row_count !== 1 ? 's' : ''} retournée{result.row_count !== 1 ? 's' : ''}
                    </div>
                  </div>
                )}

                {/* Résultat sans données (INSERT, UPDATE, DELETE, etc.) */}
                {result && result.status === 'completed' && !result.columns && (
                  <div className="mb-4 bg-green-500/10 border border-green-500/20 rounded-lg p-3">
                    <p className="text-sm text-green-400">{t('sqlEditor.querySuccess')}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SqlEditorSection;
